import OktaAuth from 'libs/okta-auth-js.min';

import { clearAuthenticationWith, OKTA_AUTHENT_TYPE, setAuthenticatedWith } from 'services/loginManager';
import { getLocale, loadItem, loadLocale, parseJwt } from 'utils';
import { clearRumUser, identifyRumUser } from 'utils/initDatadogRum';
import getLogger from 'utils/getLogger';
import { SSO_DATA } from './constants';
import { customTokenStorage, getIdToken } from './oktaTokenStorage/tokenStorage';
import { URLS } from '../../utils/constants';
import { ACCESS_TOKEN_KEY, ID_TOKEN_KEY, OKTA_STORAGE_KEY } from './oktaTokenStorage/tokenStorage.constants';

const logger = getLogger('oktaService');

let authClient: OktaAuth | null = null;

// RUM identification runs inside the Okta promise chains, whose `.catch` handlers call
// `clearAuthenticationWith()`. `parseJwt` throws on any token that is not a well-formed JWT,
// so it must never be allowed to reject here: a telemetry failure would log the user out.
const readBncIdFromAccessToken = (accessToken?: OktaAccessToken | null): string | null => {
  try {
    return accessToken?.accessToken ? parseJwt(accessToken.accessToken).sub : null;
  } catch (error) {
    logger.log('Could not parse the access token for Datadog RUM, continuing without RUM user.', error);
    return null;
  }
};

const syncRumUser = (accessToken?: OktaAccessToken | null): void => {
  try {
    const bncId = readBncIdFromAccessToken(accessToken);

    if (bncId?.trim()) {
      identifyRumUser(bncId);
      return;
    }

    clearRumUser();
  } catch (error) {
    logger.log('Datadog RUM user synchronization failed, continuing without RUM user.', error);
  }
};

/**
 * Get the current environment to be used for the SSO login.
 */
export const getCurrentEnv = (): string => {
  const currentEnv = loadItem('SSO_ENV') || loadItem('DEV_ENV') || loadItem('CURRENT_ENV');

  if (!currentEnv) {
    throw new Error('SSO environment not set');
  }
  return currentEnv && currentEnv.toUpperCase();
};

/**
 * Get the urls of okta endpoint, in case they differ from the issuer.
 * @param locale
 */
export const getCustomUrls = (
  env: string,
): {
  authorizeUrl?: string;
  userinfoUrl?: string;
  tokenUrl?: string;
  logoutUrl?: string;
  revokeUrl?: string;
} => {
  const baseUrl: string = SSO_DATA[env].BASE_URL;
  const issuerBaseUrl: string = SSO_DATA[env].ISSUER_BASE_URL;
  if (baseUrl === issuerBaseUrl) {
    return {};
  }

  const buildUrl = (path: string): string => `${baseUrl}/oauth2/${SSO_DATA[env].AUTHN_ID}${path}`;
  return {
    authorizeUrl: buildUrl(`/v1/authorize`),
    tokenUrl: buildUrl(`/v1/token`),
    // BAO-19339
    // the route in DP see https://git.bnc.ca/projects/APP5203/repos/sbip/browse/oktasso_1.8.0.yaml#2222
    logoutUrl: buildUrl(`/v1/logout`),
    revokeUrl: buildUrl(`/v1/revoke`),
    userinfoUrl: buildUrl(`/v1/userinfo`),
  };
};

/**
 * Get a singleton instance of the OktaAuth SDK client. Instantiate it if needed.
 *
 * @param locale the two-letters language code of the current language, i.e. `en` or `fr`
 */
export const getOktaClient = (locale: string): OktaAuth => {
  if (!authClient) {
    const isLocalEnv = process.env.NODE_ENV === 'development';
    const env = isLocalEnv ? 'LOCAL' : getCurrentEnv();
    const lang = locale.toUpperCase();
    const customUrls = getCustomUrls(env);
    const redirectUri = `${window.location.origin}${SSO_DATA[env].REDIRECT_URI[lang]}`;

    const httpsCookieSecureEnabled = process.env.NODE_ENV === 'production' || window.location.protocol === 'https:';
    const config: OktaAuthConstructorOptions = {
      // Required config
      issuer: `${SSO_DATA[env].ISSUER_BASE_URL}/oauth2/${SSO_DATA[env].AUTHN_ID}`,

      // custom urls for when the issuer url is not the base url of other requests
      ...customUrls,

      // Required for login flow using getWithRedirect()
      redirectUri,
      idp: SSO_DATA[env].IDP[lang],
      clientId: SSO_DATA[env].CLIENT_ID,
      responseType: SSO_DATA[env].RESPONSE_TYPE,

      // Ignore token signature verification to avoid calls to /.well-known/openid-configuration
      //  and subsequent call to /v1/keys
      ignoreSignature: true,

      // Set to true to use PKCE flow instead (defaults to true)
      // https://github.com/okta/okta-auth-js#implicit-oauth-20-flow
      pkce: false,

      // Parse authorization code from hash fragment instead of search query
      responseMode: 'fragment',
      cookies: {
        secure: httpsCookieSecureEnabled,
        sameSite: 'lax',
      },
      tokenManager: {
        secure: httpsCookieSecureEnabled,
        autoRenew: true,
        storageKey: OKTA_STORAGE_KEY,
        storage: customTokenStorage,
        sameSite: 'lax',
      },

      // Handle session expiration / token renew failure
      onSessionExpired: handleSessionExpired,
    };
    authClient = new OktaAuth(config);

    // Triggered when a token has expired
    authClient.tokenManager.on('expired', handleTokenExpired);

    // Triggered when an OAuthError is returned via the API (typically during auto-renew)
    authClient.tokenManager.on('error', handleTokenError);
  }

  return authClient;
};

/**
 * Resets the Okta SDK client instance, *for use in unit test only*
 */
export const resetOktaClient = () => {
  authClient = null;
};

export const handleSessionExpired = (...args) => {
  logger.log('session expired, re-authorization is required', ...args);
};

export const handleTokenExpired = (key: string, expiredToken: AnyOktaToken) => {
  logger.log('Token with key', key, ' has expired');

  getOktaClient(loadLocale())
    .tokenManager.get(key)
    .then((token: AnyOktaToken) => {
      logger.log(`Got token ${key} before it expires`);
      if (key === ACCESS_TOKEN_KEY && token && 'accessToken' in token) {
        syncRumUser(token as OktaAccessToken);
      }
    })
    .catch((err) => {
      logger.log(`Could not get token ${key} before it expires, deleting authentication`, err);
      clearAuthenticationWith();
      clearRumUser();
    });
};

export const handleTokenError = (err) => {
  logger.error(err);

  // err.name
  // err.message
  // err.errorCode
  // err.errorSummary
  // err.tokenKey
  // err.accessToken
  if (err.errorCode === 'login_required' && err.accessToken) {
    // The Okta session has expired or was closed outside the application
    // The application should return to an unauthenticated state
    // This error can also be handled using the 'onSessionExpired' option
    // deleteAuthent();
  }
};

export const handleParseUrlError = (err) => {
  logger.error(`parseFromUrl`, err);
  // Handle OAuthError
  clearAuthenticationWith();
};

/**
 * Get and validate the token currently saved in the token manager.
 * Returns a promise that resolves once the saved token are cleared, never rejects.
 *
 * @param locale the two-letters language code of the current language, i.e. `en` or `fr`
 */
export const checkAccessTokenValidity = (locale: string): Promise<OktaAccessToken | null> =>
  getOktaClient(locale)
    .tokenManager.get<OktaAccessToken>(ACCESS_TOKEN_KEY)
    .then((token) => {
      syncRumUser(token);
      return token;
    })
    .catch((err) => {
      clearAuthenticationWith();
      clearRumUser();
      // the user might not be authentified with okta, this is not an error per se.
      return null;
    });

/**
 * Parses the token from the url and validate it using the Okta SDK.
 *
 * @param locale the two-letters language code of the current language, i.e. `en` or `fr`
 */
export const persistUnifiedLogin = async (locale: string): Promise<void> => {
  const oktaClient = getOktaClient(locale);
  return oktaClient.token
    .parseFromUrl()
    .then(function (res) {
      const tokens = getParsedTokens(res);
      const accessToken = tokens[ACCESS_TOKEN_KEY] as OktaAccessToken | undefined;
      const idToken = tokens[ID_TOKEN_KEY] as OktaIdToken | undefined;

      if (accessToken) {
        oktaClient.tokenManager.add(ACCESS_TOKEN_KEY, accessToken);
      }

      if (idToken) {
        oktaClient.tokenManager.add(ID_TOKEN_KEY, idToken);
      }

      setAuthenticatedWith(OKTA_AUTHENT_TYPE);
      syncRumUser(accessToken || null);
    })
    .catch((err) => {
      handleParseUrlError(err);
      throw err;
    });
};

/**
 * Signout from Okta session using the redirection flow.
 */
export const signOutUnifiedLogin = async (): Promise<void> => {
  const locale = getLocale().toUpperCase();
  const bncdUrl = URLS.BNCD[locale];
  const idToken = getIdToken() || undefined;

  clearAuthenticationWith();
  clearRumUser();
  getOktaClient(loadLocale()).signOut({
    postLogoutRedirectUri: bncdUrl,
    idToken,
  });

  return Promise.resolve(undefined);
};

const getParsedTokens = (
  response: Token_ParseFromUrlResponse,
): Partial<Record<typeof ACCESS_TOKEN_KEY | typeof ID_TOKEN_KEY, AnyOktaToken>> => {
  if (response?.tokens) {
    return response.tokens as Partial<Record<typeof ACCESS_TOKEN_KEY | typeof ID_TOKEN_KEY, AnyOktaToken>>;
  }

  const tokens = ((response as unknown) as AnyOktaToken[]) || [];
  return tokens.reduce((acc, token) => {
    if (token && 'idToken' in token) {
      acc[ID_TOKEN_KEY] = token;
    } else if (token && 'accessToken' in token) {
      acc[ACCESS_TOKEN_KEY] = token;
    }
    return acc;
  }, {} as Partial<Record<typeof ACCESS_TOKEN_KEY | typeof ID_TOKEN_KEY, AnyOktaToken>>);
};
